import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Trash2 } from 'lucide-react';

import { $bulkDeleteFiles } from '../../_state/fileActions.state';
import { $showBulkDeleteDialog } from '../../_state/fileDialogs.state';
import {
  $files,
  $selectedFileIds,
  $clearSelection,
} from '../../_state/files.state';
import { useTranslations } from '../../i18n/useTranslations';

export const FileBulkDeleteDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useAtom($showBulkDeleteDialog);
  const selectedFileIds = useAtomValue($selectedFileIds);
  const files = useAtomValue($files);
  const bulkDelete = useSetAtom($bulkDeleteFiles);
  const clearSelection = useSetAtom($clearSelection);

  const selectedFiles = files.filter((f) => selectedFileIds.has(f.id));
  const folderCount = selectedFiles.filter(
    (f) => f.type === 'Directory',
  ).length;
  const fileCount = selectedFiles.filter((f) => f.type === 'Regular').length;

  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      const results = await bulkDelete({
        fileIds: Array.from(selectedFileIds),
      });

      // Count successes and failures
      const successCount = results.filesDeleteFiles.succeeded.length;
      const failedCount = results.filesDeleteFiles.failed.length;

      if (successCount > 0) {
        const message =
          successCount === 1
            ? t.dialogs.bulkDelete.successDeleted
            : t.dialogs.bulkDelete.successDeletedPlural;
        toast.success(message.replace('{count}', String(successCount)));
      }
      if (failedCount > 0) {
        const message =
          failedCount === 1
            ? t.dialogs.bulkDelete.errorFailed
            : t.dialogs.bulkDelete.errorFailedPlural;
        toast.error(message.replace('{count}', String(failedCount)));
      }

      clearSelection();
      setIsOpen(false);
    } catch (error) {
      toast.error(
        t.dialogs.bulkDelete.errorGeneral.replace(
          '{error}',
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className="@FileBulkDeleteDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Trash2 className="size-5 text-failure" />
            {(selectedFileIds.size === 1
              ? t.dialogs.bulkDelete.title
              : t.dialogs.bulkDelete.titlePlural
            ).replace('{count}', String(selectedFileIds.size))}
            ?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 p-4">
          <p>{t.dialogs.bulkDelete.message}</p>

          <ul className="text-muted-foreground text-sm">
            {fileCount > 0 && (
              <li>
                {(fileCount === 1
                  ? t.dialogs.bulkDelete.fileCount
                  : t.dialogs.bulkDelete.fileCountPlural
                ).replace('{count}', String(fileCount))}
              </li>
            )}
            {folderCount > 0 && (
              <li>
                {(folderCount === 1
                  ? t.dialogs.bulkDelete.folderCount
                  : t.dialogs.bulkDelete.folderCountPlural
                ).replace('{count}', String(folderCount))}
              </li>
            )}
          </ul>

          {folderCount > 0 && (
            <p className="text-sm font-medium text-failure">
              {t.dialogs.bulkDelete.warningFolders}
            </p>
          )}

          <p className="text-muted-foreground text-sm">
            {t.dialogs.bulkDelete.warningPermanent}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isDeleting}
          >
            {t.dialogs.bulkDelete.cancel}
          </Button>
          <Button
            variant="destructive"
            onPress={handleDelete}
            isDisabled={isDeleting}
            isLoading={isDeleting}
          >
            {t.dialogs.bulkDelete.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
